// src/core/di/ServiceContainer.ts

/**
 * Simple Dependency Injection Container
 * Manages service lifecycles and dependencies
 */

type ServiceFactory<T> = () => T;
type ServiceInstance = any;

class ServiceContainer {
    private static instance: ServiceContainer;
    private services: Map<string, ServiceInstance> = new Map();
    private factories: Map<string, ServiceFactory<any>> = new Map();
    private singletons: Set<string> = new Set();

    private constructor() {
        console.log('🔷 ServiceContainer: Initialized');
    }

    public static getInstance(): ServiceContainer {
        if (!ServiceContainer.instance) {
            ServiceContainer.instance = new ServiceContainer();
        }
        return ServiceContainer.instance;
    }

    /**
     * Register a service factory
     * @param name - Service identifier
     * @param factory - Function that creates the service
     * @param singleton - If true, only one instance is created
     */
    public register<T>(name: string, factory: ServiceFactory<T>, singleton = true): void {
        console.log(`🔷 ServiceContainer: Registering service "${name}" (singleton: ${singleton})`);
        this.factories.set(name, factory);
        if (singleton) {
            this.singletons.add(name);
        }
    }

    resolve<T>(identifier: ServiceIdentifier): T {
        // If it's a singleton and already created, return cached instance
        if (this.singletons.has(identifier) && this.services.has(identifier)) {
            return this.services.get(identifier) as T;
        }

        // Get the factory
        const factory = this.factories.get(identifier);
        if (!factory) {
            throw new Error(`Service not registered: ${String(identifier)}`);
        }

        // Create the service
        const service = factory();

        // Cache if singleton
        if (this.singletons.has(identifier)) {
            this.services.set(identifier, service);
        }

        return service as T;
    }

    /**
     * Get a service instance
     * @param name - Service identifier
     */
    public get<T>(name: string): T {
        // Return existing singleton instance
        if (this.singletons.has(name) && this.services.has(name)) {
            return this.services.get(name) as T;
        }

        // Get factory
        const factory = this.factories.get(name);
        if (!factory) {
            throw new Error(`Service "${name}" not registered`);
        }

        // Create instance
        console.log(`🔷 ServiceContainer: Creating instance of "${name}"`);
        const instance = factory();

        // Store singleton
        if (this.singletons.has(name)) {
            this.services.set(name, instance);
        }

        return instance as T;
    }

    /**
     * Check if service is registered
     */
    public has(name: string): boolean {
        return this.factories.has(name);
    }

    /**
     * Register a concrete instance (useful for testing)
     */
    public registerInstance<T>(name: string, instance: T): void {
        console.log(`🔷 ServiceContainer: Registering instance "${name}"`);
        this.services.set(name, instance);
        this.singletons.add(name);
    }

    /**
     * Clear all services (useful for testing)
     */
    public clear(): void {
        console.log('🔷 ServiceContainer: Clearing all services');
        this.services.clear();
        this.factories.clear();
        this.singletons.clear();
    }

    /**
     * Get all registered service names
     */
    public getRegisteredServices(): string[] {
        return Array.from(this.factories.keys());
    }
}

// Export singleton instance
export const container = ServiceContainer.getInstance();

// Service identifiers (type-safe keys)
export const SERVICE_IDENTIFIERS = {
    ERROR_HANDLER: 'ErrorHandler',
    ANALYTICS: 'Analytics',
    NOTIFICATION: 'Notification'
} as const;

export type ServiceIdentifier = (typeof SERVICE_IDENTIFIERS)[keyof typeof SERVICE_IDENTIFIERS];
